import { ReceiptTextIcon } from 'lucide-react';
import { useParams } from 'react-router';

import type { BillDTO } from '@invenco/common-interface/accounts';

import { DetailGroup } from '@/components/detail-group';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { DetailMain, DetailSide, Page, PageContent } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';
import { useGateways } from '@/gateways/GatewayProvider';
import { useEntityDetailsQuery } from '@/shared/hooks/queries';
import { billStatusType } from '@/shared/status-maps';
import { billStatusTitle } from '@/shared/title-maps';

import { BillActions } from './bill-actions';
import { ChargesCard } from './charges-card';
import { DetailsSection } from './details-section';
import { HoldsCard } from './holds-card';
import { InvoiceSection } from './invoice-section';

export function BillDetailsShad() {
  const { id } = useParams<{ id: string }>();
  const { accountsGateway } = useGateways();

  const {
    data: bill,
    isLoading,
    isError,
  } = useEntityDetailsQuery<BillDTO>({
    parentKey: 'bills',
    id,
    query: (billId, { signal }) => accountsGateway.getBill(billId, { signal }),
  });

  return (
    <Page>
      <Header loading={isLoading}>
        <HeaderIcon parentUrl="/bills" parentTitle="Bills">
          <ReceiptTextIcon />
        </HeaderIcon>
        <HeaderTitle pageTitle={bill?.name ?? undefined} className="flex items-center gap-2">
          <span>{bill?.name}</span>
          {bill ? (
            <StatusBadge status={bill.status} type={billStatusType} title={billStatusTitle} />
          ) : null}
        </HeaderTitle>
        <HeaderActions>{bill ? <BillActions bill={bill} /> : null}</HeaderActions>
      </Header>
      <PageContent variant="split">
        <DetailMain>
          <HoldsCard bill={bill} isLoading={isLoading} isError={isError} />
          <ChargesCard bill={bill} isLoading={isLoading} />
        </DetailMain>
        <DetailSide>
          <DetailGroup>
            <DetailsSection bill={bill} isLoading={isLoading} />
            <InvoiceSection bill={bill} isLoading={isLoading} />
          </DetailGroup>
        </DetailSide>
      </PageContent>
    </Page>
  );
}
